import * as React from 'react';

import type { ProductLookupRow } from '@graphql/queries/PerceptionModelQueries';
import { Box, Chip, Typography } from '@mui/material';
import { ResponsiveRadar } from '@nivo/radar';

import { topTokens } from './normSetUtils';

/**
 * Expanded detail for one candidate: its ggvar profile against the anchor
 * mean, plus the tokens reviewers actually used.
 */

type Props = {
  row: ProductLookupRow;
  anchorMean: Record<string, number>;
  attributes: string[];
};

const TokenChips: React.FC<{
  label: string;
  tokens: Record<string, number> | null;
}> = ({ label, tokens }) => {
  const top = topTokens(tokens, 10);
  return (
    <Box sx={{ mb: 1 }}>
      <Typography variant="caption" sx={{ color: 'text.secondary', mr: 1 }}>
        {label}
      </Typography>
      {top.length === 0 ? (
        <Typography variant="caption" sx={{ color: 'text.disabled' }}>
          none above threshold
        </Typography>
      ) : (
        top.map(([token, prevalence]) => (
          <Chip
            key={token}
            size="small"
            label={`${token} ${(prevalence * 100).toFixed(0)}%`}
            sx={{ mr: 0.5, mb: 0.5 }}
          />
        ))
      )}
    </Box>
  );
};

const ProductDetailPanel: React.FC<Props> = ({
  row,
  anchorMean,
  attributes,
}) => {
  const data = attributes.map((attr) => {
    const v = row.ggvars?.[attr]?.intensity;
    const ref = anchorMean[attr];
    return {
      attribute: attr,
      // Missing must not plot as 0 — that reads as a deep spike toward the
      // centre, i.e. "wildly off profile", when it only means "no data".
      // Mirrors the hatching in GgvarStrip.
      [row.productName]: Number.isFinite(v) ? v : null,
      'Anchor mean': Number.isFinite(ref) ? ref : null,
    };
  });

  const missing = attributes.filter(
    (attr) => !Number.isFinite(row.ggvars?.[attr]?.intensity),
  );

  return (
    <Box sx={{ display: 'flex', gap: 3, p: 2, flexWrap: 'wrap' }}>
      <Box sx={{ width: 380, height: 320 }}>
        <ResponsiveRadar
          data={data}
          keys={[row.productName, 'Anchor mean']}
          indexBy="attribute"
          margin={{ top: 50, right: 60, bottom: 30, left: 60 }}
          gridLabelOffset={12}
          dotSize={4}
          fillOpacity={0.2}
          colors={['#c1542e', '#3a7ca5']}
          legends={[
            {
              anchor: 'top-left',
              direction: 'column',
              translateX: -50,
              translateY: -40,
              itemWidth: 120,
              itemHeight: 16,
              symbolSize: 10,
            },
          ]}
        />
      </Box>

      <Box sx={{ flex: 1, minWidth: 260 }}>
        {missing.length > 0 && (
          <Typography
            variant="caption"
            sx={{ display: 'block', color: 'text.secondary', mb: 1 }}
          >
            No data for {missing.length} attribute
            {missing.length === 1 ? '' : 's'}: {missing.join(', ')}
          </Typography>
        )}
        <TokenChips label="Flavor" tokens={row.tokensFlavor} />
        <TokenChips label="Texture" tokens={row.tokensTexture} />
        <TokenChips label="Sensation" tokens={row.tokensSensation} />
      </Box>
    </Box>
  );
};

export default ProductDetailPanel;
